/**
 * Validation utilities for ProcessDefinition
 */

/**
 * Validate a ProcessDefinition and return array of error messages
 */
export function validateProcess(processJSON) {
  const errors = [];

  // Validate process metadata
  if (!processJSON.name || processJSON.name.trim() === '') {
    errors.push('Process name is required');
  }

  // Validate nodes exist
  if (!processJSON.nodes || processJSON.nodes.length === 0) {
    errors.push('Process must have at least one node');
    return errors; // Can't validate further without nodes
  }

  // Collect all node IDs
  const nodeIds = new Set(processJSON.nodes.map(n => n.node_id));
  const nodeMap = new Map(processJSON.nodes.map(n => [n.node_id, n]));

  // Validate each node
  processJSON.nodes.forEach((node, index) => {
    const nodeLabel = node.name || node.node_id || `Node ${index + 1}`;

    // Validate node ID
    if (!node.node_id || node.node_id.trim() === '') {
      errors.push(`${nodeLabel}: Node ID is required`);
    }

    // Validate node name
    if (!node.name || node.name.trim() === '') {
      errors.push(`${nodeLabel}: Node name is required`);
    }

    // Validate execution type
    const validExecutionTypes = [
      'llm', 'snn_pipeline', 'http', 'python',
      'case_extractor', 'chain_reasoner', 'cross_link',
    ];
    if (!validExecutionTypes.includes(node.execution_type)) {
      errors.push(`${nodeLabel}: Invalid execution type "${node.execution_type}"`);
    }

    // Validate LLM node specific fields
    if (node.execution_type === 'llm') {
      if (!node.prompt_template || node.prompt_template.trim() === '') {
        errors.push(`${nodeLabel}: Prompt template is required for LLM nodes`);
      }

      // Check for undefined template variables
      const templateVars = extractTemplateVariables(node.prompt_template);
      const inputNames = new Set((node.inputs || []).map(i => i.name));
      
      templateVars.forEach(varName => {
        if (!inputNames.has(varName)) {
          errors.push(`${nodeLabel}: Template variable "{{${varName}}}" has no matching input`);
        }
      });
    }

    // Validate Platform pipeline node specific fields
    if (node.execution_type === 'snn_pipeline') {
      if (!node.snn_config) {
        errors.push(`${nodeLabel}: Platform config is required for Platform pipeline nodes`);
      } else {
        const nlQueryInput = node.snn_config.nl_query_input || 'nl_query';
        const inputNames = (node.inputs || []).map(i => i.name);
        
        if (!inputNames.includes(nlQueryInput)) {
          errors.push(`${nodeLabel}: Platform config references input "${nlQueryInput}" which doesn't exist`);
        }
      }
    }

    // Validate Case Extractor node specific fields
    if (node.execution_type === 'case_extractor') {
      const cfg = node.case_extractor_config;
      if (!cfg) {
        errors.push(`${nodeLabel}: case_extractor_config is required`);
      } else {
        const caseTextInput = cfg.case_text_input || 'case_text';
        const inputNames = (node.inputs || []).map(i => i.name);
        if (!inputNames.includes(caseTextInput)) {
          errors.push(`${nodeLabel}: case_extractor_config references input "${caseTextInput}" which doesn't exist`);
        }
        if (!cfg.llm_model || String(cfg.llm_model).trim() === '') {
          errors.push(`${nodeLabel}: case_extractor_config.llm_model is required`);
        }
      }
    }

    // Validate HTTP node specific fields
    if (node.execution_type === 'http') {
      const cfg = node.http_config;
      if (!cfg) {
        errors.push(`${nodeLabel}: http_config is required for HTTP nodes`);
      } else {
        if (!cfg.url || String(cfg.url).trim() === '') {
          errors.push(`${nodeLabel}: http_config.url is required`);
        }
        const validMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
        if (cfg.method && !validMethods.includes(String(cfg.method).toUpperCase())) {
          errors.push(`${nodeLabel}: http_config.method must be one of ${validMethods.join(', ')}`);
        }
        const validBodyModes = ['none', 'json', 'raw', 'form'];
        if (cfg.body_mode && !validBodyModes.includes(cfg.body_mode)) {
          errors.push(`${nodeLabel}: http_config.body_mode must be one of ${validBodyModes.join(', ')}`);
        }
        const validResp = ['json', 'text', 'status_only'];
        if (cfg.response_mode && !validResp.includes(cfg.response_mode)) {
          errors.push(`${nodeLabel}: http_config.response_mode must be one of ${validResp.join(', ')}`);
        }
        const validAuth = ['none', 'basic', 'bearer', 'header', 'api_key_query'];
        if (cfg.auth_type && !validAuth.includes(cfg.auth_type)) {
          errors.push(`${nodeLabel}: http_config.auth_type must be one of ${validAuth.join(', ')}`);
        }
        if (cfg.auth_type && cfg.auth_type !== 'none' && !cfg.auth_credential_ref) {
          errors.push(`${nodeLabel}: auth_credential_ref is required when auth_type is "${cfg.auth_type}"`);
        }
        if ((cfg.auth_type === 'header' || cfg.auth_type === 'api_key_query') && !cfg.auth_param_name) {
          errors.push(`${nodeLabel}: auth_param_name is required when auth_type is "${cfg.auth_type}"`);
        }
        // URL template variables must resolve to declared inputs
        const inputNames = new Set((node.inputs || []).map((i) => i.name));
        extractTemplateVariables(cfg.url).forEach((v) => {
          if (!inputNames.has(v)) {
            errors.push(`${nodeLabel}: URL template variable "{{${v}}}" has no matching input`);
          }
        });
      }
    }

    // Validate Python node specific fields
    if (node.execution_type === 'python') {
      const cfg = node.python_config;
      if (!cfg) {
        errors.push(`${nodeLabel}: python_config is required for Python nodes`);
      } else {
        if (!cfg.code || String(cfg.code).trim() === '') {
          errors.push(`${nodeLabel}: python_config.code is required`);
        }
        if (!cfg.entrypoint || String(cfg.entrypoint).trim() === '') {
          errors.push(`${nodeLabel}: python_config.entrypoint is required`);
        } else if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(cfg.entrypoint)) {
          errors.push(`${nodeLabel}: python_config.entrypoint "${cfg.entrypoint}" is not a valid Python identifier`);
        }
        if (cfg.cpu_seconds !== undefined && (cfg.cpu_seconds < 1 || cfg.cpu_seconds > 600)) {
          errors.push(`${nodeLabel}: python_config.cpu_seconds must be 1-600`);
        }
        if (cfg.wall_seconds !== undefined && (cfg.wall_seconds < 1 || cfg.wall_seconds > 900)) {
          errors.push(`${nodeLabel}: python_config.wall_seconds must be 1-900`);
        }
        if (cfg.memory_mb !== undefined && (cfg.memory_mb < 32 || cfg.memory_mb > 8192)) {
          errors.push(`${nodeLabel}: python_config.memory_mb must be 32-8192`);
        }
      }
    }

    // Validate Cross-Link node specific fields
    if (node.execution_type === 'cross_link') {
      const cfg = node.cross_link_config;
      if (!cfg) {
        errors.push(`${nodeLabel}: cross_link_config is required for cross-link nodes`);
      } else {
        const tt = cfg.target_type || 'process';
        if (!['process', 'graph', 'router'].includes(tt)) {
          errors.push(`${nodeLabel}: cross_link_config.target_type must be one of process, graph, router`);
        }
        if (tt === 'process' && !cfg.target_process_id) {
          errors.push(`${nodeLabel}: cross_link_config.target_process_id is required when target_type='process'`);
        }
        if (tt === 'router' && !cfg.router_graph_id) {
          errors.push(`${nodeLabel}: cross_link_config.router_graph_id is required when target_type='router'`);
        }
        if (tt === 'graph' || tt === 'router') {
          const nlInput = cfg.nl_query_input || 'nl_query';
          const inputNames = (node.inputs || []).map(i => i.name);
          if (!inputNames.includes(nlInput)) {
            errors.push(`${nodeLabel}: cross_link_config references input "${nlInput}" which doesn't exist`);
          }
        }
      }
    }

    // Validate Chain Reasoner node specific fields
    if (node.execution_type === 'chain_reasoner') {
      const cfg = node.chain_reasoner_config;
      if (!cfg) {
        errors.push(`${nodeLabel}: chain_reasoner_config is required`);
      } else {
        const isMulti = !!cfg.pack_registry_path || cfg.mode === 'multi_pack';
        const isSingle = !!cfg.pack_path || cfg.mode === 'single_pack';
        if (!isMulti && !isSingle) {
          errors.push(`${nodeLabel}: chain_reasoner_config requires either pack_registry_path (multi-pack) or pack_path (single-pack)`);
        }
        if (isMulti && !cfg.manifest_path) {
          errors.push(`${nodeLabel}: chain_reasoner_config.manifest_path is required for multi-pack mode`);
        }
        const validResolvers = ['snn_pipeline', 'keyword'];
        if (cfg.resolver && !validResolvers.includes(cfg.resolver)) {
          errors.push(`${nodeLabel}: chain_reasoner_config.resolver must be one of ${validResolvers.join(', ')}`);
        }
        const caseEvidenceInput = cfg.case_evidence_input || 'case_evidence_frame';
        const inputNames = (node.inputs || []).map(i => i.name);
        if (!inputNames.includes(caseEvidenceInput)) {
          errors.push(`${nodeLabel}: chain_reasoner_config.case_evidence_input "${caseEvidenceInput}" not declared on inputs`);
        }
      }
    }

    // Validate inputs
    (node.inputs || []).forEach((input, inputIndex) => {
      if (!input.name || input.name.trim() === '') {
        errors.push(`${nodeLabel}: Input ${inputIndex + 1} name is required`);
      }
      
      // Check for duplicate input names
      const inputCounts = {};
      (node.inputs || []).forEach(i => {
        inputCounts[i.name] = (inputCounts[i.name] || 0) + 1;
      });
      
      if (inputCounts[input.name] > 1) {
        errors.push(`${nodeLabel}: Duplicate input name "${input.name}"`);
      }
    });

    // Validate outputs
    (node.outputs || []).forEach((output, outputIndex) => {
      if (!output.name || output.name.trim() === '') {
        errors.push(`${nodeLabel}: Output ${outputIndex + 1} name is required`);
      }
    });
  });

  // Check for duplicate node IDs
  const nodeIdCounts = {};
  processJSON.nodes.forEach(n => {
    nodeIdCounts[n.node_id] = (nodeIdCounts[n.node_id] || 0) + 1;
  });
  
  Object.entries(nodeIdCounts).forEach(([nodeId, count]) => {
    if (count > 1) {
      errors.push(`Duplicate node ID: "${nodeId}"`);
    }
  });

  // Validate synapses
  (processJSON.synapses || []).forEach((synapse, index) => {
    const synapseLabel = synapse.synapse_id || `Synapse ${index + 1}`;

    // Validate source node exists
    if (!nodeIds.has(synapse.source_node)) {
      errors.push(`${synapseLabel}: Source node "${synapse.source_node}" does not exist`);
    }

    // Validate target node exists
    if (!nodeIds.has(synapse.target_node)) {
      errors.push(`${synapseLabel}: Target node "${synapse.target_node}" does not exist`);
    }

    // Validate output mapping
    if (synapse.output_mapping && Object.keys(synapse.output_mapping).length > 0) {
      const sourceNode = nodeMap.get(synapse.source_node);
      const targetNode = nodeMap.get(synapse.target_node);

      if (sourceNode && targetNode) {
        const sourceOutputs = new Set((sourceNode.outputs || []).map(o => o.name));
        const targetInputs = new Set((targetNode.inputs || []).map(i => i.name));

        Object.entries(synapse.output_mapping).forEach(([sourceOutput, targetInput]) => {
          if (!sourceOutputs.has(sourceOutput)) {
            errors.push(`${synapseLabel}: Source output "${sourceOutput}" does not exist on source node`);
          }
          if (!targetInputs.has(targetInput)) {
            errors.push(`${synapseLabel}: Target input "${targetInput}" does not exist on target node`);
          }
        });
      }
    }

    // Validate condition syntax (basic check)
    if (synapse.condition) {
      const conditionVars = extractTemplateVariables(synapse.condition);
      // Conditions should reference variables from the context
    }
  });

  // Validate gates
  (processJSON.gates || []).forEach((gate, index) => {
    const gateLabel = gate.gate_id || `Gate ${index + 1}`;

    // Validate gate type
    const validGateTypes = ['AND', 'OR', 'THRESHOLD'];
    if (!validGateTypes.includes(gate.gate_type)) {
      errors.push(`${gateLabel}: Invalid gate type "${gate.gate_type}"`);
    }

    // Validate threshold for THRESHOLD gates
    if (gate.gate_type === 'THRESHOLD') {
      if (gate.threshold === undefined || gate.threshold === null) {
        errors.push(`${gateLabel}: Threshold value is required for THRESHOLD gates`);
      } else if (gate.threshold < 1) {
        errors.push(`${gateLabel}: Threshold must be at least 1`);
      } else if (gate.input_nodes && gate.threshold > gate.input_nodes.length) {
        errors.push(`${gateLabel}: Threshold (${gate.threshold}) cannot exceed number of input nodes (${gate.input_nodes.length})`);
      }
    }

    // Validate input nodes exist
    (gate.input_nodes || []).forEach(inputNode => {
      if (!nodeIds.has(inputNode)) {
        errors.push(`${gateLabel}: Input node "${inputNode}" does not exist`);
      }
    });

    // Validate output nodes exist
    (gate.output_nodes || []).forEach(outputNode => {
      if (!nodeIds.has(outputNode)) {
        errors.push(`${gateLabel}: Output node "${outputNode}" does not exist`);
      }
    });

    // Gates should have at least one input and one output
    if (!gate.input_nodes || gate.input_nodes.length === 0) {
      errors.push(`${gateLabel}: Gate must have at least one input node`);
    }
    if (!gate.output_nodes || gate.output_nodes.length === 0) {
      errors.push(`${gateLabel}: Gate must have at least one output node`);
    }
  });

  // Check for entry points (nodes with no incoming edges or gate outputs)
  const targetNodes = new Set();
  (processJSON.synapses || []).forEach(s => targetNodes.add(s.target_node));
  (processJSON.gates || []).forEach(g => (g.output_nodes || []).forEach(n => targetNodes.add(n)));

  const entryNodes = processJSON.nodes.filter(n => !targetNodes.has(n.node_id));
  if (entryNodes.length === 0) {
    errors.push('Process has no entry points (all nodes have incoming connections - possible cycle)');
  }

  // Check for orphan nodes (no incoming or outgoing)
  const sourceNodes = new Set();
  (processJSON.synapses || []).forEach(s => sourceNodes.add(s.source_node));
  (processJSON.gates || []).forEach(g => (g.input_nodes || []).forEach(n => sourceNodes.add(n)));

  const orphanNodes = processJSON.nodes.filter(n => 
    !targetNodes.has(n.node_id) && !sourceNodes.has(n.node_id) && processJSON.nodes.length > 1
  );
  
  orphanNodes.forEach(node => {
    errors.push(`Node "${node.name || node.node_id}" has no connections (orphan node)`);
  });

  // Check for basic cycles (simple detection)
  const cycleErrors = detectCycles(processJSON.nodes, processJSON.synapses);
  errors.push(...cycleErrors);

  return errors;
}

/**
 * Extract template variables from a string (e.g., {{variable_name}})
 */
function extractTemplateVariables(template) {
  if (!template) return [];
  
  const matches = template.match(/\{\{([^}]+)\}\}/g) || [];
  return matches.map(m => m.replace(/\{\{|\}\}/g, '').trim());
}

/**
 * Basic cycle detection using DFS
 */
function detectCycles(nodes, synapses) {
  const errors = [];
  const nodeIds = nodes.map(n => n.node_id);
  
  // Build adjacency list
  const adjacency = {};
  nodeIds.forEach(id => adjacency[id] = []);
  
  synapses.forEach(synapse => {
    if (adjacency[synapse.source_node]) {
      adjacency[synapse.source_node].push(synapse.target_node);
    }
  });

  // DFS to detect cycles
  const visited = new Set();
  const recursionStack = new Set();

  function dfs(nodeId, path = []) {
    if (recursionStack.has(nodeId)) {
      // Found a cycle
      const cycleStart = path.indexOf(nodeId);
      const cycle = path.slice(cycleStart);
      cycle.push(nodeId);
      return cycle;
    }

    if (visited.has(nodeId)) {
      return null;
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);

    const neighbors = adjacency[nodeId] || [];
    for (const neighbor of neighbors) {
      const cycle = dfs(neighbor, [...path]);
      if (cycle) {
        return cycle;
      }
    }

    recursionStack.delete(nodeId);
    return null;
  }

  for (const nodeId of nodeIds) {
    if (!visited.has(nodeId)) {
      const cycle = dfs(nodeId);
      if (cycle) {
        errors.push(`Cycle detected: ${cycle.join(' → ')}`);
        break; // Report only first cycle found
      }
    }
  }

  return errors;
}

/**
 * Validate that required inputs have mappings or defaults
 */
export function validateInputMappings(processJSON) {
  const errors = [];
  const nodeMap = new Map(processJSON.nodes.map(n => [n.node_id, n]));

  // Build map of what outputs are available to each node
  const incomingSynapses = {};
  (processJSON.synapses || []).forEach(synapse => {
    if (!incomingSynapses[synapse.target_node]) {
      incomingSynapses[synapse.target_node] = [];
    }
    incomingSynapses[synapse.target_node].push(synapse);
  });

  processJSON.nodes.forEach(node => {
    const nodeLabel = node.name || node.node_id;
    const requiredInputs = (node.inputs || []).filter(i => i.required && !i.default);
    
    requiredInputs.forEach(input => {
      // Check if this input is mapped from any incoming synapse
      const incoming = incomingSynapses[node.node_id] || [];
      const isMapped = incoming.some(syn => {
        const mapping = syn.output_mapping || {};
        return Object.values(mapping).includes(input.name);
      });

      if (!isMapped) {
        errors.push(`${nodeLabel}: Required input "${input.name}" has no mapping and no default value`);
      }
    });
  });

  return errors;
}

export default {
  validateProcess,
  validateInputMappings,
};
